import React, { useEffect, useState, createContext, useContext } from 'react';
interface AuthContextType {
  isAuthenticated: boolean;
  role: 'instructor' | 'planner' | 'manager';
  userName: string;
  userId: string;
  isDemoUser: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  setRole: (role: 'instructor' | 'planner' | 'manager') => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true); // Default to true for demo
  const [role, setRole] = useState<'instructor' | 'planner' | 'manager'>('instructor');
  const [userName, setUserName] = useState<string>('Demo User');
  const [userId, setUserId] = useState<string>('1'); // Default to first user
  const [isDemoUser, setIsDemoUser] = useState<boolean>(true);
  // Load auth state from localStorage on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('auth');
    if (savedAuth) {
      const authData = JSON.parse(savedAuth);
      setIsAuthenticated(authData.isAuthenticated);
      setRole(authData.role);
      setUserName(authData.userName);
      setUserId(authData.userId);
      setIsDemoUser(authData.isDemoUser);
    }
  }, []);
  // Save auth state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('auth', JSON.stringify({
      isAuthenticated,
      role,
      userName,
      userId,
      isDemoUser
    }));
  }, [isAuthenticated, role, userName, userId, isDemoUser]);
  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    // In a real app, this would call an API
    // For demo, we'll just simulate a successful login
    setIsAuthenticated(true);
    // Set role based on username (for demo)
    if (username.toLowerCase().includes('instructor')) {
      setRole('instructor');
      setUserId('1'); // Erik de Vries
    } else if (username.toLowerCase().includes('planner')) {
      setRole('planner');
      setUserId('3'); // Planner001
    } else if (username.toLowerCase().includes('manager')) {
      setRole('manager');
      setUserId('4'); // Manager001
    } else {
      // Default to instructor for demo
      setRole('instructor');
      setUserId('1');
    }
    setUserName(username);
    setIsDemoUser(true);
    return true;
  };
  // Logout function
  const logout = () => {
    setIsAuthenticated(false);
    setRole('instructor');
    setUserName('');
    setUserId('');
    setIsDemoUser(false);
    localStorage.removeItem('auth');
  };
  // Change role function (for demo)
  const changeRole = (newRole: 'instructor' | 'planner' | 'manager') => {
    setRole(newRole);
    // Set userId based on role
    if (newRole === 'instructor') {
      setUserId('1'); // Erik de Vries
    } else if (newRole === 'planner') {
      setUserId('3'); // Planner001
    } else if (newRole === 'manager') {
      setUserId('4'); // Manager001
    }
  };
  return <AuthContext.Provider value={{
    isAuthenticated,
    role,
    userName,
    userId,
    isDemoUser,
    login,
    logout,
    setRole: changeRole
  }} data-id="element-3704">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}